import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';

/**
 * About page component for the BagABoard application.
 * 
 * This component provides information about the company, services,
 * team, and mission statement.
 * 
 * @author BagABoard Team
 */
function About() {
  return (
    <Container className="py-5">
      {/* Hero Section */}
      <Row className="mb-5">
        <Col lg={8} className="mx-auto text-center">
          <h1 className="display-4 fw-bold mb-4">About BagAboard</h1>
          <p className="lead">
            We're revolutionizing the way travelers ship their luggage worldwide. 
            Founded with a simple mission: make travel lighter and more convenient for everyone.
          </p>
        </Col>
      </Row>

      {/* Mission Section */}
      <Row className="mb-5">
        <Col lg={6}>
          <Card className="border-0 h-100">
            <Card.Body className="p-4">
              <div className="text-primary mb-3">
                <i className="fas fa-bullseye fa-3x"></i>
              </div>
              <h3>Our Mission</h3>
              <p className="text-muted">
                To provide travelers with a reliable, secure, and affordable luggage shipping 
                service that eliminates the stress of carrying heavy bags during travel. We believe 
                everyone deserves to travel light and focus on creating memories, not managing luggage.
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="border-0 h-100">
            <Card.Body className="p-4">
              <div className="text-primary mb-3">
                <i className="fas fa-eye fa-3x"></i>
              </div>
              <h3>Our Vision</h3>
              <p className="text-muted">
                To become the world's most trusted luggage shipping platform, connecting travelers 
                across all continents with seamless, door-to-door delivery services. We envision 
                a future where heavy luggage never limits your travel adventures.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Values Section */}
      <Row className="mb-5">
        <Col>
          <h2 className="text-center mb-4">Our Values</h2>
          <Row>
            <Col md={4} className="mb-4">
              <div className="text-center">
                <div className="text-primary mb-3">
                  <i className="fas fa-shield-alt fa-2x"></i>
                </div>
                <h5>Security First</h5>
                <p className="text-muted">
                  Your belongings are precious to us. We use advanced tracking, 
                  comprehensive insurance, and secure handling protocols.
                </p>
              </div>
            </Col>
            <Col md={4} className="mb-4">
              <div className="text-center">
                <div className="text-primary mb-3">
                  <i className="fas fa-heart fa-2x"></i>
                </div>
                <h5>Customer Care</h5>
                <p className="text-muted">
                  24/7 support, transparent communication, and going above and 
                  beyond to ensure your satisfaction with every shipment.
                </p>
              </div>
            </Col>
            <Col md={4} className="mb-4">
              <div className="text-center">
                <div className="text-primary mb-3">
                  <i className="fas fa-leaf fa-2x"></i>
                </div>
                <h5>Sustainability</h5>
                <p className="text-muted">
                  Committed to eco-friendly practices, carbon-neutral shipping 
                  options, and reducing the environmental impact of travel.
                </p>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Story Section */}
      <Row className="mb-5">
        <Col lg={8} className="mx-auto">
          <Card className="border-0 bg-light">
            <Card.Body className="p-5">
              <h3 className="text-center mb-4">Our Story</h3>
              <p>
                BagAboard was born from a simple frustration experienced by millions of travelers: 
                the hassle of managing heavy luggage during trips. Our founders, frequent business 
                travelers themselves, realized there had to be a better way.
              </p>
              <p>
                After missing a connecting flight due to overweight baggage fees and long check-in 
                lines, they envisioned a service that would allow travelers to send their bags 
                ahead, arriving refreshed and ready to enjoy their destination.
              </p>
              <p className="mb-0">
                Today, we are helping millions of travelers ship their bags securely. We're proud to be making travel more enjoyable, one bag at a time.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Contact CTA */}
      <Row>
        <Col lg={8} className="mx-auto text-center">
          <Card className="border-0">
            <Card.Body className="p-5">
              <h3 className="mb-3">Ready to Travel Light?</h3>
              <p className="text-muted mb-4">
                Join thousands of satisfied customers who trust BagAboard with their luggage shipping needs.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <a href="/register" className="btn btn-primary btn-lg">
                  <i className="fas fa-user-plus me-2"></i>
                  Get Started
                </a>
                <a href="/contact" className="btn btn-outline-primary btn-lg">
                  <i className="fas fa-envelope me-2"></i>
                  Contact Us
                </a>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default About;
